import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import {
  type FiltersViewModel,
  type FilterViewModel,
} from '@/infra/FiltersViewModel'
import {
  AvailableFiltersContainer,
  FilterEditorContainer,
} from '@/components/FilterControls/FilterControlsContainer'
import { AppliedFilters, FilterButton, FilterChip } from './FilterBar'

interface FilterBarContainerProps<TFilters extends Record<string, unknown>> {
  vm: FiltersViewModel<TFilters>
  /**
   * Things to do with the applied filters, at the end of their row.
   */
  actions?: ReactNode
  /**
   * Composes the test IDs of the filter editors.
   */
  editorTestId?: string
  testId?: string
}

/**
 * Toggles the available filters, showing how many are applied.
 */
export const FilterButtonContainer = observer(function FilterButtonContainer<
  TFilters extends Record<string, unknown>,
>({ vm, testId }: FilterBarContainerProps<TFilters>) {
  return (
    <FilterButton
      appliedFilterCount={vm.appliedFilterCount}
      testId={testId && `${testId}-controls-toggle`}
    >
      <AvailableFiltersContainer vm={vm} testId={testId} />
    </FilterButton>
  )
})

/**
 * Lists the applied filters, with a way to clear them all.
 */
export const AppliedFiltersContainer = observer(
  function AppliedFiltersContainer<TFilters extends Record<string, unknown>>({
    vm,
    actions,
    editorTestId,
    testId,
  }: FilterBarContainerProps<TFilters>) {
    if (vm.explicitFilterVms.length === 0) {
      return null
    }

    return (
      <AppliedFilters
        onClearAll={vm.clearExplicitFilters}
        actions={actions}
        testId={testId && `${testId}-clear-all-button`}
      >
        {vm.explicitFilterVms.map((filter) => (
          <FilterChipContainer
            key={filter.key}
            filter={filter}
            onRemove={() => vm.clearFilter(filter.key)}
            editorTestId={editorTestId}
            testId={testId && `${testId}-chip-${filter.key}`}
          />
        ))}
      </AppliedFilters>
    )
  },
)

interface FilterChipContainerProps {
  filter: FilterViewModel<string, unknown>
  onRemove: () => void
  editorTestId?: string
  testId?: string
}

const FilterChipContainer = observer(function FilterChipContainer({
  filter,
  onRemove,
  editorTestId,
  testId,
}: FilterChipContainerProps) {
  return (
    <FilterChip
      field={filter.title}
      value={filter.valueText || 'Any'}
      isEditing={filter.editing}
      onStartEditing={filter.startEditing}
      onStopEditing={filter.stopEditing}
      onRemove={onRemove}
      testId={testId}
    >
      <FilterEditorContainer filter={filter} testId={editorTestId} />
    </FilterChip>
  )
})
